import { useEffect, useState } from 'react';
import { CheckCircle, XCircle, WarningCircle, Info, X } from '@phosphor-icons/react';
import { useToast } from '../../context/ToastContext';

/**
 * ToastContainer - Renders all active toasts
 * Position: bottom-right on desktop, bottom-center on mobile
 */
export function ToastContainer() {
  const { toasts, dismissToast } = useToast();

  if (toasts.length === 0) return null;

  return (
    <div 
      className="fixed bottom-0 right-0 z-50 p-4 flex flex-col gap-2 max-w-full sm:max-w-sm w-full pointer-events-none"
      role="region"
      aria-label="Notifications"
    >
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          toast={toast}
          onDismiss={() => dismissToast(toast.id)}
        />
      ))}
    </div>
  );
}

/**
 * Individual Toast component with enter/exit animations
 */
function Toast({ toast, onDismiss }) {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  // Animate in on mount
  useEffect(() => {
    requestAnimationFrame(() => {
      setIsVisible(true);
    });
  }, []);

  // Handle dismiss with exit animation
  function handleDismiss() {
    setIsExiting(true);
    setTimeout(onDismiss, 200); // Wait for exit animation
  }

  // Handle action button click
  function handleAction() {
    if (toast.onAction) {
      toast.onAction();
    }
    handleDismiss();
  }

  const { icon: Icon, colors } = getToastStyle(toast.type);
  
  // Use assertive for errors (more urgent), polite for others
  const ariaLive = toast.type === 'error' ? 'assertive' : 'polite';

  return (
    <div
      className={`
        pointer-events-auto w-full rounded-lg shadow-lg overflow-hidden
        transform transition-all duration-200 ease-out
        ${isVisible && !isExiting ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}
      `}
      style={{
        backgroundColor: 'var(--color-bg-canvas)',
        border: `1px solid ${colors.border}`,
      }}
      role="alert"
      aria-live={ariaLive}
    >
      <div className="p-4 flex items-start gap-3">
        {/* Icon */}
        <div 
          className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center"
          style={{ backgroundColor: colors.bgLight }}
          aria-hidden="true"
        >
          <Icon size={16} weight="fill" style={{ color: colors.icon }} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {toast.title && (
            <p 
              className="text-sm font-medium mb-0.5"
              style={{ color: 'var(--color-text-primary)' }}
            >
              {toast.title}
            </p>
          )}
          <p 
            className="text-sm"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            {toast.message}
          </p>
          
          {/* Action button */}
          {toast.action && (
            <button
              onClick={handleAction}
              className="mt-2 text-sm font-medium hover:underline"
              style={{ color: colors.icon }}
            >
              {toast.action}
            </button>
          )}
        </div>

        {/* Dismiss button */}
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 p-1 rounded-sm transition-colors hover:bg-black/5"
          aria-label="Dismiss"
        >
          <X size={16} style={{ color: 'var(--color-text-muted)' }} />
        </button>
      </div>
    </div>
  );
}

/**
 * Get icon and colors for toast type
 */
function getToastStyle(type) {
  switch (type) {
    case 'success':
      return {
        icon: CheckCircle,
        colors: {
          icon: 'var(--color-status-success)',
          bgLight: 'rgba(78, 143, 106, 0.1)',
          border: 'rgba(78, 143, 106, 0.3)',
        },
      };
    case 'error':
      return {
        icon: XCircle,
        colors: {
          icon: 'var(--color-status-error)',
          bgLight: 'rgba(193, 96, 92, 0.1)',
          border: 'rgba(193, 96, 92, 0.3)',
        },
      };
    case 'warning':
      return {
        icon: WarningCircle,
        colors: {
          icon: '#D97706',
          bgLight: 'rgba(217, 119, 6, 0.1)',
          border: 'rgba(217, 119, 6, 0.3)',
        },
      };
    case 'info':
    default:
      return {
        icon: Info,
        colors: {
          icon: 'var(--color-brand-primary)',
          bgLight: 'rgba(46, 63, 83, 0.1)',
          border: 'rgba(46, 63, 83, 0.3)',
        },
      };
  }
}

export default ToastContainer;
